import { API_URL } from '../env';
import type { Document, Folder, LibraryContent } from '../types/library';

const HANDBOOK_PARTITION = 'handbook';

function getToken(): string {
    return localStorage.getItem('token') ?? '';
}

function authHeaders(): HeadersInit {
    return {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
    };
}

async function parseResponse<T>(res: Response): Promise<T> {
    const json = await res.json();
    if (!res.ok || json.status === false) {
        throw new Error(json.message ?? json.error ?? `HTTP ${res.status}`);
    }
    return json;
}

// Folder
function isSystemOwned(rolePartition?: string): boolean {
    if (!rolePartition) return false;
    const r = rolePartition.toLowerCase();
    return r.includes('admin') || r.includes('system') || r.includes('handbook');
}

export async function getFolderById(folderId: number): Promise<Folder> {
    const res = await fetch(`${API_URL}api/v1/be/folders/${folderId}`, { headers: authHeaders() });
    const json = await parseResponse<{ status: boolean; data: Folder }>(res);
    return json.data;
}

export async function getLibraryContents(parentId?: number | null): Promise<LibraryContent> {
    const params = new URLSearchParams();
    if (parentId != null) params.set('parent_id', String(parentId));
    const res = await fetch(`${API_URL}api/v1/be/library/contents?${params}`, { headers: authHeaders() });
    const json = await parseResponse<{ status: boolean; data: LibraryContent }>(res);

    const data = json.data;
    return {
        ...data,
        folders: data.folders.filter(f => isSystemOwned(f.role_partition) || f.is_system),
        documents: data.documents.filter(d => isSystemOwned(d.role_partition) || d.is_system),
    };
}

export async function createFolder(name: string, color: string, parentId?: number | null): Promise<Folder> {
    const body: Record<string, unknown> = { name, color };
    if (parentId != null) body.parent_id = parentId;
    const res = await fetch(`${API_URL}api/v1/be/folders`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });
    const json = await parseResponse<{ status: boolean; data: Folder }>(res);
    return json.data;
}

export async function updateFolder(folderId: number, name: string, color: string): Promise<Folder> {
    const res = await fetch(`${API_URL}api/v1/be/folders/${folderId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ name, color }),
    });
    const json = await parseResponse<{ status: boolean; data: Folder }>(res);
    return json.data;
}

export async function deleteFolder(folderId: number): Promise<void> {
    const res = await fetch(`${API_URL}api/v1/be/folders/${folderId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    await parseResponse(res);
}

// Document 
export async function uploadDocument(
    file: File,
    folderId?: number | null,
    isHandbook?: boolean,
    thumbnail?: File | null,
): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId != null) formData.append('folder_id', String(folderId));
    if (isHandbook) formData.append('partition', HANDBOOK_PARTITION);
    if (thumbnail) formData.append('thumbnail', thumbnail);

    const res = await fetch(`${API_URL}api/v1/be/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
    });
    const json = await parseResponse<{ status: boolean; data: Document }>(res);
    return json.data;
}

export async function getDownloadUrl(documentId: number): Promise<string> {
    const res = await fetch(`${API_URL}api/v1/be/documents/${documentId}/download`, {
        headers: authHeaders(),
    });
    const json = await parseResponse<{ status: boolean; data: { download_url: string } }>(res);
    return json.data.download_url;
}

export async function deleteDocument(documentId: number): Promise<void> {
    const res = await fetch(`${API_URL}api/v1/be/documents/${documentId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    await parseResponse(res);
}

export async function renameDocument(documentId: number, name: string, folderId?: number | null): Promise<Document> {
    const body: Record<string, unknown> = { name };
    if (folderId !== undefined) body.folder_id = folderId;
    const res = await fetch(`${API_URL}api/v1/be/documents/${documentId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });
    const json = await parseResponse<{ status: boolean; data: Document }>(res);
    return json.data;
}

export async function updateThumbnail(documentId: number, file: File): Promise<Document> {
    const formData = new FormData();
    formData.append('thumbnail', file);
    formData.append('_method', 'PUT');
    const res = await fetch(`${API_URL}api/v1/be/documents/${documentId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
    });
    const json = await parseResponse<{ status: boolean; data: Document }>(res);
    return json.data;
}

export function getThumbnailUrl(documentId: number): string {
    return `${API_URL}api/v1/be/handbook/${documentId}/thumbnail`;
}

export async function moveDocument(documentId: number, targetFolderId: number | null): Promise<Document> {
    const res = await fetch(`${API_URL}api/v1/be/documents/${documentId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ folder_id: targetFolderId }),
    });
    const json = await parseResponse<{ status: boolean; data: Document }>(res);
    return json.data;
}

export async function moveFolder(folderId: number, targetFolderId: number | null): Promise<Folder> {
    const res = await fetch(`${API_URL}api/v1/be/folders/${folderId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ parent_id: targetFolderId }),
    });
    const json = await parseResponse<{ status: boolean; data: Folder }>(res);
    return json.data;
}
